// ============================================
// 계단 기록 컴포넌트 (심플 HTML 스타일)
// ============================================
import React, { useState, useEffect } from 'react';
import { db, auth, storage, analytics, logEvent } from '../firebase';
import { collection, getDocs, addDoc, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import StairProposal from './StairProposal';

function StairRecord() {
  const [location, setLocation] = useState(null);
  const [nearbyStairs, setNearbyStairs] = useState([]);
  const [recommendedStairs, setRecommendedStairs] = useState([]);
  const [selectedStair, setSelectedStair] = useState('');
  const [selectedStairData, setSelectedStairData] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showProposal, setShowProposal] = useState(false);
  const [stairCooldowns, setStairCooldowns] = useState({});
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionSteps, setCorrectionSteps] = useState('');
  const [correctionContent, setCorrectionContent] = useState('');
  const [submittingCorrection, setSubmittingCorrection] = useState(false);

  const [justRecorded, setJustRecorded] = useState(false);
  const [lastRecordedStair, setLastRecordedStair] = useState(null);
  const [lastPhotoURL, setLastPhotoURL] = useState(null);
  const [sameStationStairs, setSameStationStairs] = useState([]);
  const [reusePhoto, setReusePhoto] = useState(true);

  const [showPraise, setShowPraise] = useState(false);
  const [praiseMessage, setPraiseMessage] = useState('');
  const [recordedSteps, setRecordedSteps] = useState(0);
  const [showStairDropdown, setShowStairDropdown] = useState(false);

  const COOLDOWN_MINUTES = 10;
  const COOLDOWN_MS = COOLDOWN_MINUTES * 60 * 1000;

  const PRAISE_MESSAGES = [
    '잘하셨습니다!',
    '대단해요!',
    '훌륭합니다!',
    '계단왕이 되셨군요!',
    '오늘도 건강 챙기셨네요!',
    '멋져요!',
    '최고예요!',
    '계속 이 기세로!',
    '정말 대단하세요!',
    '건강 챙기는 당신, 최고!',
    '꾸준함이 힘입니다!',
    '멋진 기록이에요!'
  ];

  // 초기 로드
  useEffect(() => {
    getLocation();
    loadAllStairCooldowns();
  }, []);

  // 계단 선택 시 선택 데이터 유지
  useEffect(() => {
    if (selectedStair) {
      const stairData = nearbyStairs.find(s => s.id === selectedStair);
      setSelectedStairData(stairData);
      setShowCorrectionForm(false);
      setCorrectionSteps('');
      setCorrectionContent('');

      logEvent(analytics, 'select_stair', {
        stair_id: selectedStair,
        stair_name: stairData?.name,
        steps: stairData?.steps
      });
    }
  }, [selectedStair, nearbyStairs]);

  // 쿨타임 카운트다운
  useEffect(() => {
    if (selectedStair && stairCooldowns[selectedStair]) {
      const lastTime = stairCooldowns[selectedStair];
      const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - lastTime));
      setCooldownRemaining(remaining);

      if (remaining > 0) {
        const timer = setInterval(() => {
          const newRemaining = Math.max(0, COOLDOWN_MS - (Date.now() - lastTime));
          setCooldownRemaining(newRemaining);
          if (newRemaining <= 0) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
      }
    } else {
      setCooldownRemaining(0);
    }
  }, [selectedStair, stairCooldowns]);

  // 쿨타임 로드
  const loadAllStairCooldowns = async () => {
    try {
      const recordsQuery = query(
        collection(db, 'user_records'),
        where('userId', '==', auth.currentUser.uid),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(recordsQuery);
      const cooldownMap = {};

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const stairId = data.stairId;
        const recordTime = data.timestamp.toMillis();
        if (!cooldownMap[stairId]) {
          cooldownMap[stairId] = recordTime;
        }
      });

      setStairCooldowns(cooldownMap);
    } catch (error) {
      console.error('쿨타임 로드 실패:', error);
    }
  };

  const getStairCooldown = (stairId) => {
    if (!stairCooldowns[stairId]) return 0;
    const lastTime = stairCooldowns[stairId];
    const elapsed = Date.now() - lastTime;
    return Math.max(0, COOLDOWN_MS - elapsed);
  };

  // 제안된 계단 바로 기록하기
  const handleRecordStairFromProposal = (stairData) => {
    setMessage('계단이 자동 승인되면 목록에 나타납니다. 잠시 후 새로고침해보세요!');

    // 3초 후 계단 목록 다시 로드
    setTimeout(() => {
      loadNearbyStairs();
      setMessage('');

      // 승인된 계단 찾아서 자동 선택 시도
      setTimeout(() => {
        const matchedStair = nearbyStairs.find(s =>
          s.name === stairData.name && s.station === stairData.station
        );
        if (matchedStair) {
          setSelectedStair(matchedStair.id);
          setMessage('✅ 계단이 자동 승인되었습니다! 이제 기록하세요.');
        }
      }, 1000);
    }, 3000);
  };

  const formatCooldownTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}분 ${seconds}초`;
  };

  // 위치 가져오기
  const getLocation = () => {
    setMessage('현재 위치를 확인 중입니다.');

    if (!navigator.geolocation) {
      setMessage('GPS를 지원하지 않는 브라우저입니다.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ lat: latitude, lng: longitude });
        loadNearbyStairs(latitude, longitude);
        setMessage('');
      },
      (error) => {
        setMessage('위치 권한을 허용해 주세요.');
        console.error(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // 근처 계단 불러오기
  const loadNearbyStairs = async (lat, lng) => {
    try {
      const stairsSnapshot = await getDocs(collection(db, 'stairs'));
      const allStairs = stairsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const stairsWithDistance = allStairs
        .map(stair => ({
          ...stair,
          distance: calculateDistance(lat, lng, stair.lat, stair.lng)
        }))
        .sort((a, b) => a.distance - b.distance);

      const nearby = stairsWithDistance.filter(s => s.distance <= 100);
      const recommended = stairsWithDistance.filter(s => s.distance > 100 && s.distance <= 1000);

      setNearbyStairs(nearby);
      setRecommendedStairs(recommended);
    } catch (error) {
      console.error('계단 로드 실패:', error);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return Math.round(R * c);
  };

  const formatDistance = (distance) => {
    return distance >= 1000 
      ? `${(distance / 1000).toFixed(1)}km`
      : `${distance}m`;
  };

  // 사진 선택
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setMessage('사진 크기는 10MB 이하여야 합니다.');
        return;
      }
      setPhoto(file);
      setMessage('');
      logEvent(analytics, 'select_photo', {
        file_size: file.size
      });
    }
  };

  // 기록 제출
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStair) {
      setMessage('계단을 선택해 주세요.');
      return;
    }

    const photoToUse = (reusePhoto && lastPhotoURL) ? null : photo;

    if (!photoToUse && !lastPhotoURL) {
      setMessage('사진을 선택해 주세요.');
      return;
    }

    const cooldown = getStairCooldown(selectedStair);
    if (cooldown > 0) {
      setMessage(`이 계단은 ${formatCooldownTime(cooldown)} 후에 다시 기록할 수 있습니다.`);
      return;
    }

    setLoading(true);
    setMessage('기록을 저장 중입니다.');

    try {
      const stairData = nearbyStairs.find(s => s.id === selectedStair);
      let photoUrl;

      if (reusePhoto && lastPhotoURL) {
        photoUrl = lastPhotoURL;
      } else {
        const photoRef = ref(storage, `stair_photos/${auth.currentUser.uid}/${Date.now()}_${photo.name}`);
        await uploadBytes(photoRef, photo);
        photoUrl = await getDownloadURL(photoRef);
      }

      await addDoc(collection(db, 'user_records'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        stairId: selectedStair,
        stairName: stairData.name,
        steps: stairData.steps,
        photoUrl: photoUrl,
        location: {
          lat: location.lat,
          lng: location.lng
        },
        timestamp: new Date()
      });

      setStairCooldowns(prev => ({
        ...prev,
        [selectedStair]: Date.now()
      }));

      setJustRecorded(true);
      setLastRecordedStair(stairData);
      setLastPhotoURL(photoUrl);

      const sameStation = nearbyStairs.filter(s =>
        s.station === stairData.station &&
        s.id !== selectedStair &&
        !stairCooldowns[s.id]
      );
      setSameStationStairs(sameStation);

      setMessage(`계단 ${stairData.steps}개 기록이 저장되었습니다.`);

      // 칭찬 팝업 표시
      const randomPraise = PRAISE_MESSAGES[Math.floor(Math.random() * PRAISE_MESSAGES.length)];
      setPraiseMessage(randomPraise);
      setRecordedSteps(stairData.steps);
      setShowPraise(true);

      logEvent(analytics, 'record_stair', {
        stair_id: selectedStair,
        stair_name: stairData.name,
        steps: stairData.steps,
        reused_photo: reusePhoto && lastPhotoURL
      });

      if (!reusePhoto) {
        setSelectedStair('');
        setPhoto(null);
        const input = document.getElementById('photo-input');
        if (input) input.value = '';
      }

    } catch (error) {
      setMessage('기록 저장에 실패했습니다. 다시 시도해 주세요.');
      console.error(error);
      logEvent(analytics, 'record_stair_failed', {
        error: error.message
      });
    }

    setLoading(false);
  };

  // 연속 기록용 계단 변경
  const handleQuickRecord = (stairId) => {
    logEvent(analytics, 'quick_record_select', {
      stair_id: stairId
    });
    setSelectedStair(stairId);
    setJustRecorded(false);
    setReusePhoto(true);
    setMessage('');
  };

  const handleCancelQuickRecord = () => {
    setJustRecorded(false);
    setLastRecordedStair(null);
    setLastPhotoURL(null);
    setSameStationStairs([]);
    setSelectedStair('');
    setPhoto(null);
    setReusePhoto(true);
    const input = document.getElementById('photo-input');
    if (input) input.value = '';
  };

  // 계단 정보 수정 건의
  const handleCorrectionSubmit = async () => {
    if (!correctionContent.trim()) {
      setMessage('수정 내용을 입력해 주세요.');
      return;
    }

    if (!selectedStairData) {
      setMessage('계단을 먼저 선택해 주세요.');
      return;
    }

    setSubmittingCorrection(true);

    try {
      await addDoc(collection(db, 'feedback'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        title: `계단 정보 수정: ${selectedStairData.name}`,
        content: `[계단 정보 수정 건의]

계단: ${selectedStairData.name}
현재 개수: ${selectedStairData.steps}개
${correctionSteps ? `제안 개수: ${correctionSteps}개\n` : ''}
수정 내용:
${correctionContent}`,
        stairId: selectedStairData.id,
        stairName: selectedStairData.name,
        currentSteps: selectedStairData.steps,
        suggestedSteps: correctionSteps || null,
        type: 'stair_correction',
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      setMessage('수정 건의가 접수되었습니다.');
      setShowCorrectionForm(false);
      setCorrectionSteps('');
      setCorrectionContent('');
      setTimeout(() => setMessage(''), 3000);

      logEvent(analytics, 'submit_stair_correction', {
        stair_id: selectedStairData.id,
        stair_name: selectedStairData.name,
        has_suggested_steps: !!correctionSteps
      });
    } catch (error) {
      setMessage('제출에 실패했습니다. 다시 시도해 주세요.');
      console.error(error);
    }

    setSubmittingCorrection(false);
  };

  const handleCorrectionCancel = () => {
    setShowCorrectionForm(false);
    setCorrectionSteps('');
    setCorrectionContent('');
  };

  const isCooldownActive = selectedStair && getStairCooldown(selectedStair) > 0;

  // 렌더링
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>계단 기록하기</h2>

      {message && (
        <div style={styles.message}>{message}</div>
      )}

      {!showProposal && location && (
        <>
          {/* 연속 기록 안내 박스 */}
          {justRecorded && sameStationStairs.length > 0 && (
            <div style={styles.quickBox}>
              <div style={styles.quickTitle}>방금 기록이 저장되었습니다.</div>
              {lastRecordedStair && (
                <p style={styles.quickText}>
                  {lastRecordedStair.name} ({lastRecordedStair.steps}개)
                </p>
              )}
              <p style={styles.quickSubTitle}>같은 역의 다른 계단도 바로 기록할 수 있습니다.</p>

              <div style={styles.quickStairList}>
                {sameStationStairs.map(stair => (
                  <button
                    key={stair.id}
                    type="button"
                    onClick={() => handleQuickRecord(stair.id)}
                    style={styles.smallButton}
                  >
                    {stair.name} ({stair.steps}개)
                  </button>
                ))}
              </div>

              <div style={styles.checkboxRow}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={reusePhoto}
                    onChange={(e) => setReusePhoto(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span>이전 사진 그대로 사용하기</span>
                </label>
              </div>

              <button
                type="button"
                onClick={handleCancelQuickRecord}
                style={styles.smallButtonGray}
              >
                닫기
              </button>
            </div>
          )}

          {nearbyStairs.length > 0 ? (
            <form onSubmit={handleSubmit} style={styles.form}>
              {isCooldownActive && (
                <div style={styles.noticeBox}>
                  이 계단은 잠시 후 다시 기록할 수 있습니다.
                  <br />
                  다른 계단은 바로 기록할 수 있습니다.
                </div>
              )}

              <div style={styles.field}>
                <label style={styles.label}>
                  근처에서 기록 가능한 계단 ({nearbyStairs.length}개)
                </label>
                <div style={styles.customSelectContainer}>
                  <div
                    style={styles.customSelectHeader}
                    onClick={() => !loading && setShowStairDropdown(!showStairDropdown)}
                  >
                    {selectedStairData
                      ? `${selectedStairData.name} (${selectedStairData.steps}개)`
                      : '계단을 선택해 주세요.'
                    }
                    <span style={styles.dropdownArrow}>{showStairDropdown ? '▲' : '▼'}</span>
                  </div>
                  {showStairDropdown && (
                    <div style={styles.customSelectOptions}>
                      {nearbyStairs.map(stair => {
                        const cooldown = getStairCooldown(stair.id);
                        const hasCooldown = cooldown > 0;
                        return (
                          <div
                            key={stair.id}
                            style={{
                              ...styles.customOption,
                              ...(selectedStair === stair.id ? styles.customOptionSelected : {}),
                              ...(hasCooldown ? styles.customOptionDisabled : {})
                            }}
                            onClick={() => {
                              if (!hasCooldown) {
                                setSelectedStair(stair.id);
                                setShowStairDropdown(false);
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (!hasCooldown) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!hasCooldown) {
                                e.currentTarget.style.backgroundColor = selectedStair === stair.id ? '#e3f2fd' : '#fff';
                              }
                            }}
                          >
                            <div style={styles.optionLeft}>
                              {stair.name} ({stair.steps}개) - {formatDistance(stair.distance)}
                              {hasCooldown && <span style={styles.cooldownBadge}> 🕐 대기 {formatCooldownTime(cooldown)}</span>}
                            </div>
                            <div style={styles.optionRight}>
                              {stair.nickname || '딸기'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>계단 사진</label>
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoChange}
                  style={styles.input}
                  required
                  disabled={isCooldownActive || loading}
                />
                {photo && (
                  <p style={styles.infoText}>선택된 파일: {photo.name}</p>
                )}
                <p style={styles.warningText}>
                  실제 해당 계단 사진이 아닐 경우, 기록이 삭제될 수 있습니다.
                </p>
              </div>

              <button 
                type="submit" 
                style={{
                  ...styles.buttonPrimaryFull,
                  ...(isCooldownActive ? styles.buttonDisabled : {})
                }}
                disabled={isCooldownActive || loading}
              >
                {loading
                  ? '기록을 저장 중입니다...'
                  : isCooldownActive
                    ? `대기 중 · ${formatCooldownTime(cooldownRemaining)}`
                    : '기록하기'}
              </button>

              <button
                type="button"
                onClick={() => {
                  logEvent(analytics, 'click_new_stair_proposal');
                  setShowProposal(true);
                }}
                style={styles.buttonDefaultFull}
              >
                새로운 계단 등록 요청하기
              </button>
              <p style={styles.helpText}>
                현재는 지하철역 계단만 등록 가능합니다.
              </p>
            </form>
          ) : (
            <div style={styles.emptyBox}>
              <p style={styles.emptyMain}>100m 이내에 등록된 계단이 없습니다.</p>
              <p style={styles.emptySub}>계단 출구 근처로 이동한 뒤 다시 시도해 주세요.</p>

              <div style={styles.buttonRow}>
                <button
                  onClick={() => {
                    logEvent(analytics, 'click_refresh_location');
                    getLocation();
                  }}
                  style={styles.buttonDefault}
                >
                  위치 다시 확인하기
                </button>
                <button
                  onClick={() => {
                    logEvent(analytics, 'click_new_stair_proposal');
                    setShowProposal(true);
                  }}
                  style={styles.buttonBorder}
                >
                  새로운 계단 등록 요청
                </button>
              </div>
              <p style={styles.helpText}>
                지하철역 계단만 등록을 받고 있습니다.
              </p>
            </div>
          )}

          {/* 계단 정보 수정 건의 */}
          {selectedStairData && nearbyStairs.length > 0 && (
            <div style={styles.correctionBox}>
              <button
                type="button"
                onClick={() => {
                  logEvent(analytics, 'toggle_correction_form', {
                    show: !showCorrectionForm
                  });
                  setShowCorrectionForm(!showCorrectionForm);
                }}
                style={styles.buttonToggle}
              >
                이 계단 정보 수정 건의하기
              </button>
              
              {showCorrectionForm && (
                <div style={styles.correctionInner}>
                  <p style={styles.correctionInfo}>
                    선택한 계단: {selectedStairData.name} (현재 {selectedStairData.steps}개)
                  </p>

                  <div style={styles.field}>
                    <label style={styles.label}>수정하려는 계단 개수 (선택)</label>
                    <input
                      type="number"
                      value={correctionSteps}
                      onChange={(e) => setCorrectionSteps(e.target.value)}
                      placeholder={`예: 실제로는 250개`}
                      style={styles.input}
                      min="1"
                    />
                    <p style={styles.helpTextSmall}>
                      직접 세어 보신 개수를 적어 주세요.
                    </p>
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>수정 내용 *</label>
                    <textarea
                      value={correctionContent}
                      onChange={(e) => setCorrectionContent(e.target.value)}
                      placeholder={`예시)\n- 실제 개수가 다릅니다.\n- 출구 번호가 다릅니다.\n- 위치 설명을 이렇게 바꾸면 더 좋겠습니다.`}
                      style={styles.textarea}
                      maxLength={500}
                    />
                    <div style={styles.charCount}>
                      {correctionContent.length} / 500
                    </div>
                  </div>

                  <div style={styles.buttonRow}>
                    <button
                      type="button"
                      onClick={handleCorrectionCancel}
                      style={styles.buttonDefault}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      onClick={handleCorrectionSubmit}
                      style={styles.buttonPrimary}
                      disabled={!correctionContent.trim() || submittingCorrection}
                    >
                      {submittingCorrection ? '제출 중...' : '제출하기'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 추천 계단 */}
          {recommendedStairs.length > 0 && (
            <div style={styles.recommendBox}>
              <div style={styles.recommendTitle}>
                근처 추천 계단 ({recommendedStairs.length}개)
              </div>
              <p style={styles.recommendDesc}>
                조금만 이동하면 기록할 수 있는 계단입니다.
              </p>
              <div style={styles.recommendList}>
                {recommendedStairs.slice(0, 5).map(stair => (
                  <div key={stair.id} style={styles.recommendItem}>
                    <div>
                      <div style={styles.recommendName}>{stair.name}</div>
                      <div style={styles.recommendMeta}>
                        계단 {stair.steps}개 · 역 {stair.station}
                      </div>
                    </div>
                    <div style={styles.recommendDistance}>
                      {formatDistance(stair.distance)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {showProposal && (
        <StairProposal
          onClose={() => setShowProposal(false)}
          onRecordStair={handleRecordStairFromProposal}
        />
      )}

      {/* 칭찬 팝업 */}
      {showPraise && (
        <div style={styles.overlay} onClick={() => setShowPraise(false)}>
          <div style={styles.praiseModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.praiseTitle}>{praiseMessage}</div>
            <div style={styles.praiseSteps}>계단 {recordedSteps}개 기록 완료</div>
            <button
              onClick={() => setShowPraise(false)}
              style={styles.praiseButton}
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 스타일 (심플 HTML)
// ============================================
const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '6px'
  },
  message: {
    padding: '10px 12px',
    marginBottom: '16px',
    border: '1px solid #c8e6c9',
    backgroundColor: '#f1f8e9',
    fontSize: '13px'
  },
  form: {
    border: '1px solid #ddd',
    padding: '16px',
    fontSize: '13px'
  },
  field: {
    marginBottom: '14px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontSize: '13px',
    fontWeight: '500'
  },
  select: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  },
  customSelectContainer: {
    position: 'relative',
    width: '100%'
  },
  customSelectHeader: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  dropdownArrow: {
    fontSize: '10px',
    color: '#666'
  },
  customSelectOptions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #ccc',
    borderTop: 'none',
    backgroundColor: '#fff',
    zIndex: 1000,
    boxSizing: 'border-box'
  },
  customOption: {
    padding: '8px',
    fontSize: '13px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px'
  },
  customOptionSelected: {
    backgroundColor: '#e3f2fd'
  },
  customOptionDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#999',
    cursor: 'not-allowed'
  },
  optionLeft: {
    flex: 1,
    textAlign: 'left',
    fontSize: '13px'
  },
  optionRight: {
    fontSize: '11px',
    color: '#666',
    whiteSpace: 'nowrap'
  },
  cooldownBadge: {
    fontSize: '11px',
    color: '#999'
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    minHeight: '90px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  infoText: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#555'
  },
  warningText: {
    marginTop: '6px',
    padding: '8px',
    fontSize: '12px',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    color: '#6d4c41'
  },
  buttonPrimaryFull: {
    width: '100%',
    padding: '10px 0',
    marginTop: '4px',
    border: '1px solid #000',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  buttonDefaultFull: {
    width: '100%',
    padding: '10px 0',
    marginTop: '8px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: '13px',
    cursor: 'pointer'
  },
  helpText: {
    marginTop: '6px',
    fontSize: '11px',
    color: '#777',
    textAlign: 'center'
  },
  helpTextSmall: {
    marginTop: '4px',
    fontSize: '11px',
    color: '#777'
  },
  buttonDisabled: {
    backgroundColor: '#aaa',
    borderColor: '#aaa',
    cursor: 'not-allowed'
  },
  emptyBox: {
    border: '1px solid #ddd',
    padding: '18px',
    fontSize: '13px'
  },
  emptyMain: {
    margin: 0,
    fontSize: '14px',
    color: '#333'
  },
  emptySub: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#666'
  },
  buttonRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px'
  },
  buttonDefault: {
    flex: 1,
    padding: '9px 0',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  buttonBorder: {
    flex: 1,
    padding: '9px 0',
    border: '1px solid #000',
    backgroundColor: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  noticeBox: {
    padding: '10px 12px',
    marginBottom: '12px',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    fontSize: '12px',
    lineHeight: 1.5
  },
  quickBox: {
    border: '1px solid #90caf9',
    backgroundColor: '#e3f2fd',
    padding: '12px',
    marginBottom: '16px',
    fontSize: '13px'
  },
  quickTitle: {
    fontWeight: '600',
    marginBottom: '4px'
  },
  quickText: {
    margin: '2px 0 6px 0',
    fontSize: '13px'
  },
  quickSubTitle: {
    margin: '4px 0 6px 0',
    fontSize: '12px',
    color: '#555'
  },
  quickStairList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '8px'
  },
  checkboxRow: {
    marginBottom: '8px'
  },
  checkboxLabel: {
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  checkbox: {
    width: '14px',
    height: '14px'
  },
  smallButton: {
    width: '100%',
    padding: '8px 0',
    border: '1px solid #1e88e5',
    backgroundColor: '#fff',
    fontSize: '12px',
    cursor: 'pointer'
  },
  smallButtonGray: {
    width: '100%',
    padding: '8px 0',
    border: '1px solid #bdbdbd',
    backgroundColor: '#f5f5f5',
    fontSize: '12px',
    cursor: 'pointer',
    marginTop: '4px'
  },
  correctionBox: {
    marginTop: '18px',
    border: '1px solid #ddd',
    padding: '12px',
    fontSize: '13px'
  },
  buttonToggle: {
    width: '100%',
    padding: '9px 0',
    border: '1px solid #ccc',
    backgroundColor: '#f7f7f7',
    fontSize: '13px',
    cursor: 'pointer'
  },
  correctionInner: {
    marginTop: '10px',
    paddingTop: '8px',
    borderTop: '1px solid #eee'
  },
  correctionInfo: {
    fontSize: '12px',
    marginBottom: '8px',
    color: '#555'
  },
  charCount: {
    textAlign: 'right',
    fontSize: '11px',
    color: '#777',
    marginTop: '4px'
  },
  buttonPrimary: {
    flex: 1,
    padding: '9px 0',
    border: '1px solid #000',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  recommendBox: {
    marginTop: '18px',
    border: '1px solid #ddd',
    padding: '12px',
    fontSize: '13px'
  },
  recommendTitle: {
    fontWeight: '600',
    marginBottom: '4px'
  },
  recommendDesc: {
    fontSize: '12px',
    color: '#555',
    marginBottom: '8px'
  },
  recommendList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  recommendItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #eee',
    padding: '8px'
  },
  recommendName: {
    fontSize: '13px',
    fontWeight: '500'
  },
  recommendMeta: {
    fontSize: '11px',
    color: '#777',
    marginTop: '2px'
  },
  recommendDistance: {
    fontSize: '12px',
    fontWeight: '500'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  praiseModal: {
    backgroundColor: '#fff',
    border: '2px solid #000',
    padding: '24px',
    minWidth: '280px',
    maxWidth: '90%',
    textAlign: 'center'
  },
  praiseTitle: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '12px',
    color: '#000'
  },
  praiseSteps: {
    fontSize: '16px',
    marginBottom: '20px',
    color: '#555'
  },
  praiseButton: {
    width: '100%',
    padding: '10px 0',
    border: '1px solid #000',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600'
  }
};

export default StairRecord;
